/** A task line placed among the conversation's items. */
export type TaskThreadRow<Task> = { kind: "booroTask"; key: string; task: Task };

/**
 * Places each task in the conversation at the moment it was created: before the first item
 * that came after it. Tasks older than the first loaded item only appear once the whole
 * history is loaded, so loading earlier messages does not reshuffle the lines.
 */
export function withTaskRows<Item, Task extends { ticketId: string; createdAt: string }>(
  items: readonly Item[],
  itemTime: (item: Item) => string | undefined,
  tasks: readonly Task[],
  options: { historyComplete: boolean },
): Array<Item | TaskThreadRow<Task>> {
  if (tasks.length === 0) return [...items];
  const sorted = [...tasks].sort((a, b) =>
    a.createdAt === b.createdAt
      ? a.ticketId.localeCompare(b.ticketId)
      : a.createdAt < b.createdAt
        ? -1
        : 1,
  );
  const firstTime = items.map(itemTime).find((time) => time !== undefined);
  const rows: Array<Item | TaskThreadRow<Task>> = [];
  let next = 0;
  const row = (task: Task): TaskThreadRow<Task> => ({
    kind: "booroTask",
    key: `task:${task.ticketId}`,
    task,
  });
  if (!options.historyComplete && firstTime !== undefined) {
    while (next < sorted.length && sorted[next]!.createdAt < firstTime) next++;
  }
  for (const item of items) {
    const time = itemTime(item);
    if (time !== undefined) {
      while (next < sorted.length && sorted[next]!.createdAt < time)
        rows.push(row(sorted[next++]!));
    }
    rows.push(item);
  }
  while (next < sorted.length) rows.push(row(sorted[next++]!));
  return rows;
}
